import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";

import ModalDocumentoCropper from "./ModalDocumentoCropper";

import {
  buscarClientesRecepcion,
  postClienteRecepcion,
  subirDocumentoClienteRecepcion,
} from "../helpers/rutaClienterecepcion";

import {
  leerDocumentoClienteOCR,
} from "../helpers/rutaOcrDocumento";

import {
  completarRecepcionVenta,
} from "../helpers/rutaVentas";

/* =========================================================
   HELPERS
========================================================= */

const clean = (v) => String(v ?? "").trim();

const upper = (v) =>
  clean(v).toUpperCase();

const onlyDigits = (v) =>
  String(v ?? "").replace(/\D/g, "");

const getId = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return clean(
      v._id ??
      v.id ??
      v.value ??
      ""
    );
  }

  return clean(v);
};

const normalizarTexto = (valor = "") =>
  String(valor || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const CUBA_PROVINCIAS = [
  "Pinar del Río",
  "Artemisa",
  "La Habana",
  "Mayabeque",
  "Matanzas",
  "Cienfuegos",
  "Villa Clara",
  "Sancti Spíritus",
  "Ciego de Ávila",
  "Camagüey",
  "Las Tunas",
  "Holguín",
  "Granma",
  "Santiago de Cuba",
  "Guantánamo",
];

const obtenerZonaLogisticaPorProvincia = (
  provincia = ""
) => {
  const p = normalizarTexto(provincia);

  const mapa = {
    "LA HABANA": "PUERTO DEL MARIEL",
    "PINAR DEL RIO": "PUERTO DEL MARIEL",
    ARTEMISA: "PUERTO DEL MARIEL",
    MAYABEQUE: "PUERTO DEL MARIEL",
    MATANZAS: "PUERTO DEL MARIEL",

    CIENFUEGOS: "ZONA CENTRAL",
    "VILLA CLARA": "ZONA CENTRAL",
    "SANCTI SPIRITUS": "ZONA CENTRAL",
    "CIEGO DE AVILA": "ZONA CENTRAL",
    CAMAGUEY: "ZONA CENTRAL",
    "LAS TUNAS": "ZONA CENTRAL",

    "SANTIAGO DE CUBA": "SANTIAGO DE CUBA",
    GRANMA: "SANTIAGO DE CUBA",
    HOLGUIN: "SANTIAGO DE CUBA",
    GUANTANAMO: "SANTIAGO DE CUBA",
  };

  return mapa[p] || "";
};

const normalizarProvinciaCubaSelect = (
  provincia = ""
) => {
  const p = normalizarTexto(provincia);

  return (
    CUBA_PROVINCIAS.find(
      (prov) =>
        normalizarTexto(prov) === p
    ) || upper(provincia)
  );
};

const normalizarTelefonoCuba = (v = "") => {
  const digits = onlyDigits(v).slice(0, 10);

  if (!digits) return "";

  return `+${digits}`;
};

const esTelefonoCubaValido = (v = "") =>
  /^\+\d{10}$/.test(
    String(v || "").trim()
  );

const makeReceptorInicial = () => ({
  nombre: "",
  segundoNombre: "",
  apellido1: "",
  apellido2: "",

  carnetIdentidad: "",

  telefonoPrincipal: "+53",
  email: "",

  direccion: "",
  referenciaDireccion: "",

  provincia: "",
  municipio: "",
  zonaLogistica: "",

  pais: "CUBA",

  activo: true,
});

const buildNombreCompleto = (
  receptor = {}
) =>
  [
    receptor.nombre,
    receptor.segundoNombre,
    receptor.apellido1,
    receptor.apellido2,
  ]
    .map(upper)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const buildDireccionCompleta = (
  receptor = {}
) => {
  const direccion = upper(
    receptor.direccion
  );

  const referencia = upper(
    receptor.referenciaDireccion
  );

  return [
    direccion,

    referencia &&
    normalizarTexto(referencia) !==
      normalizarTexto(direccion)
      ? referencia
      : "",

    upper(receptor.municipio),
    upper(receptor.provincia),
    "CUBA",
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();
};

const productoTxt = (item = {}) => {
  return (
    clean(item.descripcionProducto) ||
    clean(item.modeloProducto) ||
    clean(item.producto?.nombreComercial) ||
    clean(item.producto?.modelo) ||
    `Producto ${getId(item.producto)}`
  );
};

/* =========================================================
   OCR
========================================================= */

const mapOcrAReceptor = (datos = {}) => {
  const receptor =
    datos?.receptor ||
    datos?.clienteRecepcion ||
    datos?.comprador ||
    datos?.persona ||
    {};

  const direccion =
    datos?.direccion ||
    receptor?.direccion ||
    {};

  const patch = {};

  const nombre =
    receptor.nombre ||
    receptor.firstName;

  const segundoNombre =
    receptor.segundoNombre ||
    receptor.middleName;

  const apellido1 =
    receptor.apellido1 ||
    receptor.lastName ||
    receptor.apellido;

  const apellido2 =
    receptor.apellido2 ||
    receptor.segundoApellido;

  const carnet =
    receptor.carnetIdentidad ||
    receptor.dni ||
    receptor.documento ||
    receptor.idDocumento ||
    receptor.numeroDocumento;

  if (nombre) {
    patch.nombre = upper(nombre);
  }

  if (segundoNombre) {
    patch.segundoNombre =
      upper(segundoNombre);
  }

  if (apellido1) {
    patch.apellido1 = upper(apellido1);
  }

  if (apellido2) {
    patch.apellido2 = upper(apellido2);
  }

  if (carnet) {
    patch.carnetIdentidad =
      onlyDigits(carnet).slice(0, 11);
  }

  const dir =
    direccion.direccion ||
    direccion.domicilio ||
    direccion.addressLine1 ||
    direccion.address ||
    receptor.direccion ||
    "";

  const referencia =
    direccion.referenciaDireccion ||
    direccion.referencia ||
    direccion.entreCalles ||
    direccion.entre_calle ||
    "";

  const provincia =
    direccion.provincia ||
    direccion.estado ||
    "";

  const municipio =
    direccion.municipio ||
    direccion.ciudad ||
    "";

  if (dir) {
    patch.direccion = upper(dir);
  }

  if (referencia) {
    patch.referenciaDireccion =
      upper(referencia);
  }

  if (provincia) {
    patch.provincia =
      normalizarProvinciaCubaSelect(
        provincia
      );

    patch.zonaLogistica =
      obtenerZonaLogisticaPorProvincia(
        patch.provincia
      );
  }

  if (municipio) {
    patch.municipio =
      upper(municipio);
  }

  patch.pais = "CUBA";

  return patch;
};

const mapOcrDorsoAReceptor = (
  datos = {}
) => {
  const receptor =
    datos?.receptor ||
    datos?.clienteRecepcion ||
    datos?.comprador ||
    datos?.persona ||
    {};

  const direccion =
    datos?.direccion ||
    receptor?.direccion ||
    {};

  const patch = {};

  const dir =
    direccion.direccion ||
    direccion.domicilio ||
    direccion.addressLine1 ||
    direccion.address ||
    receptor.direccion ||
    "";

  const referencia =
    direccion.referenciaDireccion ||
    direccion.referencia ||
    direccion.entreCalles ||
    direccion.entre_calle ||
    "";

  const provincia =
    direccion.provincia ||
    direccion.estado ||
    "";

  const municipio =
    direccion.municipio ||
    direccion.ciudad ||
    "";

  if (dir) {
    patch.direccion = upper(dir);
  }

  if (referencia) {
    patch.referenciaDireccion =
      upper(referencia);
  }

  if (provincia) {
    patch.provincia =
      normalizarProvinciaCubaSelect(
        provincia
      );

    patch.zonaLogistica =
      obtenerZonaLogisticaPorProvincia(
        patch.provincia
      );
  }

  if (municipio) {
    patch.municipio =
      upper(municipio);
  }

  patch.pais = "CUBA";

  return patch;
};

/* =========================================================
   COMPONENTE
========================================================= */

export default function ModalCompletarReceptor({
  show,
  venta,
  onClose,
  onSaved,
}) {
  const [ventaTrabajo, setVentaTrabajo] =
    useState(null);

  const [receptor, setReceptor] =
    useState(makeReceptorInicial());

  const [itemsSeleccionados, setItemsSeleccionados] =
    useState([]);

  const [frenteFile, setFrenteFile] =
    useState(null);

  const [frentePreview, setFrentePreview] =
    useState("");

  const [frenteDatos, setFrenteDatos] =
    useState(null);

  const [dorsoFile, setDorsoFile] =
    useState(null);

  const [dorsoPreview, setDorsoPreview] =
    useState("");

  const [dorsoDatos, setDorsoDatos] =
    useState(null);

  const [leyendoFrente, setLeyendoFrente] =
    useState(false);

  const [leyendoDorso, setLeyendoDorso] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [mensaje, setMensaje] =
    useState("");

  const [error, setError] =
    useState("");

  const [crop, setCrop] = useState({
    show: false,
    tipo: "frente",
    file: null,
  });

  /* =====================================================
     INICIALIZACIÓN
  ===================================================== */

  useEffect(() => {
    if (!show) return;

    setVentaTrabajo(venta || null);
    setReceptor(makeReceptorInicial());

    setFrenteFile(null);
    setDorsoFile(null);

    setFrenteDatos(null);
    setDorsoDatos(null);

    setFrentePreview("");
    setDorsoPreview("");

    setMensaje("");
    setError("");

    const pendientes = (
      Array.isArray(venta?.items)
        ? venta.items
        : []
    )
      .filter(
        (item) => !item.clienteRecibe
      )
      .map((item) => getId(item));

    /*
      Por defecto seleccionamos todos.
      Si son para personas distintas,
      el usuario puede destildar.
    */
    setItemsSeleccionados(
      pendientes.filter(Boolean)
    );
  }, [show, venta]);

  const itemsPendientes = useMemo(() => {
    const items = Array.isArray(
      ventaTrabajo?.items
    )
      ? ventaTrabajo.items
      : [];

    return items.filter(
      (item) => !item.clienteRecibe
    );
  }, [ventaTrabajo]);

  const nombreCompleto = useMemo(
    () => buildNombreCompleto(receptor),
    [receptor]
  );

  const direccionCompleta = useMemo(
    () => buildDireccionCompleta(receptor),
    [receptor]
  );

  /* =====================================================
     CAMPOS
  ===================================================== */

  const handleChange = (
    name,
    value
  ) => {
    setReceptor((prev) => {
      if (name === "carnetIdentidad") {
        return {
          ...prev,
          carnetIdentidad:
            onlyDigits(value).slice(0, 11),
        };
      }

      if (
        name === "telefonoPrincipal"
      ) {
        return {
          ...prev,
          telefonoPrincipal:
            normalizarTelefonoCuba(value),
        };
      }

        const next = {
        ...prev,

        /*
            Mientras escribe NO hacemos trim(),
            porque eliminaría el espacio entre palabras.
            La limpieza final ya se hace al guardar.
        */
        [name]:
            name === "email"
            ? String(value ?? "").toLowerCase()
            : String(value ?? "").toUpperCase(),
        };

      if (name === "provincia") {
        next.provincia = value;

        next.zonaLogistica =
          obtenerZonaLogisticaPorProvincia(
            value
          );
      }

      return next;
    });
  };

  const toggleItem = (itemId) => {
    setItemsSeleccionados((prev) =>
      prev.includes(itemId)
        ? prev.filter(
            (id) => id !== itemId
          )
        : [...prev, itemId]
    );
  };

  /* =====================================================
     DOCUMENTOS / CROPPER
  ===================================================== */

  const seleccionarDocumento = (
    tipo,
    e
  ) => {
    const file =
      e.target.files?.[0] || null;

    if (!file) return;

    setCrop({
      show: true,
      tipo,
      file,
    });

    e.target.value = "";
  };

  const cancelarCrop = () => {
    setCrop({
      show: false,
      tipo: "frente",
      file: null,
    });
  };

  const leerConIA = async (
    tipo,
    file
  ) => {
    if (!file) return;

    try {
      if (tipo === "frente") {
        setLeyendoFrente(true);
      } else {
        setLeyendoDorso(true);
      }

      const resp =
        await leerDocumentoClienteOCR({
          file,
          tipoDocumento: tipo,
          destino: "clienteRecepcion",
        });

      const datos =
        resp?.datos || null;

      if (!datos) {
        throw new Error(
          "La IA no devolvió datos del documento."
        );
      }

      const patch =
        tipo === "dorso"
          ? mapOcrDorsoAReceptor(datos)
          : mapOcrAReceptor(datos);

      setReceptor((prev) => ({
        ...prev,
        ...patch,
      }));

      if (tipo === "frente") {
        setFrenteDatos(datos);
      } else {
        setDorsoDatos(datos);
      }
    } catch (err) {
      console.error(
        "Error OCR receptor:",
        err?.response?.data || err
      );

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "No se pudo leer el documento."
      );
    } finally {
      setLeyendoFrente(false);
      setLeyendoDorso(false);
    }
  };

  const guardarRecorte = async ({
    file,
    previewUrl,
  }) => {
    const tipo = crop.tipo;

    setCrop({
      show: false,
      tipo: "frente",
      file: null,
    });

    setError("");

    if (tipo === "frente") {
      if (frentePreview) {
        URL.revokeObjectURL(
          frentePreview
        );
      }

      setFrenteFile(file);
      setFrentePreview(
        previewUrl || ""
      );

      await leerConIA(
        "frente",
        file
      );
    } else {
      if (dorsoPreview) {
        URL.revokeObjectURL(
          dorsoPreview
        );
      }

      setDorsoFile(file);
      setDorsoPreview(
        previewUrl || ""
      );

      await leerConIA(
        "dorso",
        file
      );
    }
  };

  /* =====================================================
     VALIDAR
  ===================================================== */

  const validar = () => {
    if (
      itemsSeleccionados.length === 0
    ) {
      return "Seleccioná al menos un producto para este receptor.";
    }

    if (
      onlyDigits(
        receptor.carnetIdentidad
      ).length !== 11
    ) {
      return "El carnet de identidad debe tener 11 dígitos.";
    }

    if (!upper(receptor.nombre)) {
      return "Completá el nombre.";
    }

    if (!upper(receptor.apellido1)) {
      return "Completá el apellido 1.";
    }

    if (
      !esTelefonoCubaValido(
        receptor.telefonoPrincipal
      )
    ) {
      return "El teléfono debe tener formato + seguido de 10 dígitos. Ej: +5355531234";
    }

    if (!upper(receptor.direccion)) {
      return "Completá la dirección.";
    }

    if (!upper(receptor.provincia)) {
      return "Seleccioná la provincia.";
    }

    if (!upper(receptor.municipio)) {
      return "Completá el municipio.";
    }

    return "";
  };

  /* =====================================================
     BUSCAR / CREAR RECEPTOR
  ===================================================== */

    const obtenerOCrearReceptor = async () => {
    const carnet = onlyDigits(
        receptor.carnetIdentidad
    );

    /*
        Volvemos a validar acá también.
        No dependemos solamente de validar()
        para una operación tan importante.
    */
    if (carnet.length !== 11) {
        throw new Error(
        "El carnet del receptor debe tener exactamente 11 dígitos."
        );
    }

    const encontrados =
        await buscarClientesRecepcion({
        q: carnet,
        limit: 10,
        });

    const listaEncontrados =
        Array.isArray(encontrados)
        ? encontrados
        : Array.isArray(encontrados?.clientes)
        ? encontrados.clientes
        : Array.isArray(
            encontrados?.clienterecepcion
            )
        ? encontrados.clienterecepcion
        : [];

    /*
        IMPORTANTE:
        Solo consideramos existente si el carnet
        coincide EXACTAMENTE.
    */
    const existente =
        listaEncontrados.find(
        (c) =>
            onlyDigits(
            c?.carnetIdentidad ||
                c?.ci ||
                c?.documento ||
                c?.idDocumento
            ) === carnet
        );

    if (existente?._id) {
        /*
        SEGURIDAD EXTRA:
        Si el carnet existe pero el nombre/apellido
        no coincide con lo que estamos viendo en
        pantalla, NO reutilizamos automáticamente
        ese receptor.
        */

        const nombreActual =
        normalizarTexto(
            receptor.nombre
        );

        const apellidoActual =
        normalizarTexto(
            receptor.apellido1
        );

        const nombreExistente =
        normalizarTexto(
            existente.nombre
        );

        const apellidoExistente =
        normalizarTexto(
            existente.apellido1
        );

        const nombreDistinto =
        nombreActual &&
        nombreExistente &&
        nombreActual !==
            nombreExistente;

        const apellidoDistinto =
        apellidoActual &&
        apellidoExistente &&
        apellidoActual !==
            apellidoExistente;

        if (
        nombreDistinto ||
        apellidoDistinto
        ) {
        const existenteTexto =
            existente.nombreCompleto ||
            [
            existente.nombre,
            existente.segundoNombre,
            existente.apellido1,
            existente.apellido2,
            ]
            .filter(Boolean)
            .join(" ");

        throw new Error(
            `ATENCIÓN: el carnet ${carnet} ya pertenece a "${existenteTexto}". ` +
            `Los datos cargados corresponden a "${nombreCompleto}". ` +
            "Revisá el documento antes de continuar."
        );
        }

        /*
        Carnet + identidad compatibles.
        Reutilizamos el receptor existente.
        */
        return {
        cliente: existente,
        creado: false,
        };
    }

    /*
        No existe: creamos uno nuevo.
    */
    const payload = {
        nombre: upper(
        receptor.nombre
        ),

        segundoNombre: upper(
        receptor.segundoNombre
        ),

        apellido1: upper(
        receptor.apellido1
        ),

        apellido2: upper(
        receptor.apellido2
        ),

        nombreCompleto,

        carnetIdentidad: carnet,

        telefonoPrincipal:
        normalizarTelefonoCuba(
            receptor.telefonoPrincipal
        ),

        email: clean(
        receptor.email
        ).toLowerCase(),

        direccion: upper(
        receptor.direccion
        ),

        referenciaDireccion: upper(
        receptor.referenciaDireccion
        ),

        provincia: upper(
        receptor.provincia
        ),

        municipio: upper(
        receptor.municipio
        ),

        zonaLogistica: upper(
        obtenerZonaLogisticaPorProvincia(
            receptor.provincia
        )
        ),

        pais: "CUBA",

        direccionCompleta,

        activo: true,
    };

    const resp =
        await postClienteRecepcion(
        payload
        );

    const creado =
        resp?.clienterecepcion ||
        resp?.cliente ||
        resp?.data?.clienterecepcion ||
        resp?.data?.cliente ||
        resp?.data ||
        resp;

    if (!getId(creado)) {
        throw new Error(
        "El receptor se guardó, pero no se pudo obtener su ID."
        );
    }

    /*
        Otra protección:
        comprobamos que el receptor que vuelve
        del backend tenga el mismo carnet.
    */
    const carnetCreado =
        onlyDigits(
        creado.carnetIdentidad ||
            creado.ci ||
            creado.documento ||
            creado.idDocumento
        );

    if (
        carnetCreado &&
        carnetCreado !== carnet
    ) {
        throw new Error(
        "El receptor devuelto por el servidor no coincide con el carnet cargado. No se asignó la venta."
        );
    }

    return {
        cliente: creado,
        creado: true,
    };
    };

  /* =====================================================
     GUARDAR
  ===================================================== */

  const guardar = async () => {
    const msg = validar();

    if (msg) {
      setError(msg);
      return;
    }

    if (!ventaTrabajo?._id) {
      setError(
        "No se encontró el ID de la venta."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMensaje("");

      /*
        1. Buscar o crear receptor.
      */
      const resultadoReceptor =
        await obtenerOCrearReceptor();

      const receptorGuardado =
        resultadoReceptor.cliente;

      const receptorId =
        getId(receptorGuardado);

      /*
        2. Subir documentación.
      */
      const erroresDocumentos = [];

      if (frenteFile) {
        try {
          await subirDocumentoClienteRecepcion(
            receptorId,
            "frente",
            frenteFile
          );
        } catch (e) {
          console.error(
            "Error subiendo frente:",
            e
          );

          erroresDocumentos.push(
            "frente"
          );
        }
      }

      if (dorsoFile) {
        try {
          await subirDocumentoClienteRecepcion(
            receptorId,
            "dorso",
            dorsoFile
          );
        } catch (e) {
          console.error(
            "Error subiendo dorso:",
            e
          );

          erroresDocumentos.push(
            "dorso"
          );
        }
      }

      /*
        3. Asignar receptor a productos.
      */
      const asignaciones =
        itemsSeleccionados.map(
          (itemId) => ({
            itemId,
            clienteRecibe:
              receptorId,
          })
        );

      const resp =
        await completarRecepcionVenta(
          ventaTrabajo._id,
          asignaciones
        );

      const ventaActualizada =
        resp?.venta ||
        resp?.data?.venta ||
        null;

      if (!ventaActualizada) {
        throw new Error(
          "La recepción se guardó, pero no se recibió la venta actualizada."
        );
      }

      setVentaTrabajo(
        ventaActualizada
      );

      onSaved?.(
        ventaActualizada
      );

      const pendientesRestantes = (
        Array.isArray(
          ventaActualizada.items
        )
          ? ventaActualizada.items
          : []
      ).filter(
        (item) =>
          !item.clienteRecibe
      );

      /*
        4. Si ya están todos completos,
        cerramos.
      */
      if (
        pendientesRestantes.length === 0
      ) {
        if (
          erroresDocumentos.length > 0
        ) {
          alert(
            `Recepción completada. Hubo problemas al subir: ${erroresDocumentos.join(
              ", "
            )}.`
          );
        }

        onClose?.();
        return;
      }

      /*
        5. Si quedan productos pendientes,
        preparamos otro receptor.
      */
      setMensaje(
        `Receptor asignado correctamente. Quedan ${pendientesRestantes.length} producto(s) sin receptor.`
      );

      setReceptor(
        makeReceptorInicial()
      );

      setFrenteFile(null);
      setDorsoFile(null);

      setFrenteDatos(null);
      setDorsoDatos(null);

      setFrentePreview("");
      setDorsoPreview("");

      setItemsSeleccionados(
        pendientesRestantes
          .map((item) =>
            getId(item)
          )
          .filter(Boolean)
      );
    } catch (err) {
      console.error(
        "Error completando recepción:",
        err?.response?.data || err
      );

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "No se pudo completar la recepción."
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <>
      <Modal
        show={show}
        onHide={
          saving
            ? undefined
            : onClose
        }
        size="xl"
        centered
        scrollable
        backdrop="static"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            Completar receptor
            {ventaTrabajo?.nroventa
              ? ` - Venta N° ${ventaTrabajo.nroventa}`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          {mensaje && (
            <Alert variant="success">
              {mensaje}
            </Alert>
          )}

          <Alert
            variant="warning"
            className="py-2"
          >
            La venta tiene productos con
            recepción pendiente. Seleccioná
            cuáles corresponden a este
            receptor.
          </Alert>

          {/* ================= PRODUCTOS ================= */}

          <div className="card mb-3">
            <div className="card-header">
              <strong>
                Productos pendientes
              </strong>
            </div>

            <div className="card-body">
              {itemsPendientes.length ===
              0 ? (
                <div className="text-success fw-bold">
                  ✓ No quedan productos
                  pendientes.
                </div>
              ) : (
                itemsPendientes.map(
                  (item, idx) => {
                    const id = getId(item);

                    const cantidad =
                      Number(item.cantidad || 1);

                    const asignaUnaUnidad =
                      cantidad > 1 &&
                      Number(
                        item.limitePorReceptorPorVenta || 0
                      ) > 0;

                    const marcado =
                      itemsSeleccionados.includes(
                        id
                      );

                    return (
                        <label
                        key={id || idx}
                        className="d-flex align-items-center gap-3 border rounded p-3 mb-2"
                        style={{
                            cursor: "pointer",
                            background: marcado
                            ? "#fff3cd"
                            : "#ffffff",
                            color: "#212529",
                        }}
                        >
                        <input
                          type="checkbox"
                          checked={
                            marcado
                          }
                          onChange={() =>
                            toggleItem(
                              id
                            )
                          }
                          disabled={
                            saving
                          }
                        />

                        <div style={{ color: "#212529" }}>
                        <strong
                            style={{
                            color: "#212529",
                            fontWeight: 700,
                            }}
                        >
                            {productoTxt(item)}
                        </strong>

                          <div
                            className="small"
                            style={{
                              color: "#6c757d",
                            }}
                          >
                            Cantidad pendiente: {cantidad}
                          </div>

                          {asignaUnaUnidad && (
                            <div
                              className="small fw-semibold mt-1"
                              style={{
                                color: "#856404",
                              }}
                            >
                              Se asignará 1 unidad a este receptor.
                              Quedarán {cantidad - 1} unidad(es)
                              pendientes.
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  }
                )
              )}
            </div>
          </div>

          {/* ================= OCR ================= */}

          <div
            className="card mb-3"
            style={{
              background:
                "#f4f7fb",
            }}
          >
            <div
              className="card-header text-white"
              style={{
                background:
                  "#242d3d",
              }}
            >
              <strong>
                Documento del receptor
              </strong>
              <div className="small">
                Frente para identidad,
                reverso para domicilio.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-3">
                {/* FRENTE */}

                <div className="col-md-6">
                  <div
                    className="p-3 rounded"
                    style={{
                      background:
                        "#111722",
                      color: "#fff",
                    }}
                  >
                    <div className="fw-bold mb-2">
                      ID receptor - frente
                    </div>

                    <label
                      className="d-flex align-items-center justify-content-center rounded"
                      style={{
                        minHeight:
                          160,
                        border:
                          "1px dashed #9b5b2c",
                        cursor:
                          "pointer",
                        background:
                          "#151d2a",
                        overflow:
                          "hidden",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          seleccionarDocumento(
                            "frente",
                            e
                          )
                        }
                        style={{
                          display:
                            "none",
                        }}
                        disabled={
                          saving
                        }
                      />

                      {frentePreview ? (
                        <img
                          src={
                            frentePreview
                          }
                          alt="Frente receptor"
                          style={{
                            maxWidth:
                              "100%",
                            maxHeight:
                              150,
                            objectFit:
                              "contain",
                          }}
                        />
                      ) : (
                        <div className="text-center">
                          <div
                            style={{
                              fontSize:
                                28,
                            }}
                          >
                            📷
                          </div>

                          <strong>
                            Subir
                            fotografía
                          </strong>

                          <div className="small text-muted">
                            Nombre,
                            apellido y
                            carnet
                          </div>
                        </div>
                      )}
                    </label>

                    <div className="mt-2">
                      {leyendoFrente ? (
                        <div className="text-info">
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Leyendo con IA...
                        </div>
                      ) : frenteDatos ? (
                        <div className="text-success fw-bold">
                          ✓ Identidad
                          leída
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* DORSO */}

                <div className="col-md-6">
                  <div
                    className="p-3 rounded"
                    style={{
                      background:
                        "#111722",
                      color: "#fff",
                    }}
                  >
                    <div className="fw-bold mb-2">
                      ID receptor - reverso
                    </div>

                    <label
                      className="d-flex align-items-center justify-content-center rounded"
                      style={{
                        minHeight:
                          160,
                        border:
                          "1px dashed #9b5b2c",
                        cursor:
                          "pointer",
                        background:
                          "#151d2a",
                        overflow:
                          "hidden",
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          seleccionarDocumento(
                            "dorso",
                            e
                          )
                        }
                        style={{
                          display:
                            "none",
                        }}
                        disabled={
                          saving
                        }
                      />

                      {dorsoPreview ? (
                        <img
                          src={
                            dorsoPreview
                          }
                          alt="Reverso receptor"
                          style={{
                            maxWidth:
                              "100%",
                            maxHeight:
                              150,
                            objectFit:
                              "contain",
                          }}
                        />
                      ) : (
                        <div className="text-center">
                          <div
                            style={{
                              fontSize:
                                28,
                            }}
                          >
                            📷
                          </div>

                          <strong>
                            Subir
                            fotografía
                          </strong>

                          <div className="small text-muted">
                            Dirección,
                            provincia y
                            municipio
                          </div>
                        </div>
                      )}
                    </label>

                    <div className="mt-2">
                      {leyendoDorso ? (
                        <div className="text-info">
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Leyendo con IA...
                        </div>
                      ) : dorsoDatos ? (
                        <div className="text-success fw-bold">
                          ✓ Dirección
                          leída
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= DATOS ================= */}

          <div className="card">
            <div className="card-header">
              <strong>
                Datos del receptor
              </strong>
            </div>

            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-3">
                  <label className="form-label">
                    Carnet *
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.carnetIdentidad
                    }
                    onChange={(e) =>
                      handleChange(
                        "carnetIdentidad",
                        e.target.value
                      )
                    }
                    maxLength={11}
                    inputMode="numeric"
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">
                    Teléfono *
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.telefonoPrincipal
                    }
                    onChange={(e) =>
                      handleChange(
                        "telefonoPrincipal",
                        e.target.value
                      )
                    }
                    maxLength={11}
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">
                    Nombre *
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.nombre
                    }
                    onChange={(e) =>
                      handleChange(
                        "nombre",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">
                    Segundo nombre
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.segundoNombre
                    }
                    onChange={(e) =>
                      handleChange(
                        "segundoNombre",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">
                    Apellido 1 *
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.apellido1
                    }
                    onChange={(e) =>
                      handleChange(
                        "apellido1",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">
                    Apellido 2
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.apellido2
                    }
                    onChange={(e) =>
                      handleChange(
                        "apellido2",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Nombre completo
                  </label>

                  <input
                    className="form-control bg-light"
                    value={
                      nombreCompleto
                    }
                    readOnly
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label">
                    Dirección *
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.direccion
                    }
                    onChange={(e) =>
                      handleChange(
                        "direccion",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Provincia *
                  </label>

                  <select
                    className="form-control"
                    value={
                      receptor.provincia
                    }
                    onChange={(e) =>
                      handleChange(
                        "provincia",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  >
                    <option value="">
                      Elija provincia
                    </option>

                    {CUBA_PROVINCIAS.map(
                      (prov) => (
                        <option
                          key={prov}
                          value={prov}
                        >
                          {prov}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Municipio *
                  </label>

                  <input
                    className="form-control"
                    value={
                      receptor.municipio
                    }
                    onChange={(e) =>
                      handleChange(
                        "municipio",
                        e.target.value
                      )
                    }
                    disabled={
                      saving
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    País
                  </label>

                  <input
                    className="form-control bg-light"
                    value="CUBA"
                    readOnly
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label">
                    Dirección completa
                  </label>

                  <input
                    className="form-control bg-light"
                    value={
                      direccionCompleta
                    }
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            variant="success"
            onClick={guardar}
            disabled={
              saving ||
              itemsPendientes.length === 0
            }
          >
            {saving ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Guardando...
              </>
            ) : (
              "Guardar receptor"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalDocumentoCropper
        show={crop.show}
        file={crop.file}
        titulo={
          crop.tipo === "frente"
            ? "Editor frente documento receptor"
            : "Editor reverso documento receptor"
        }
        subtitulo={
          crop.tipo === "frente"
            ? "Ajustá el área de identidad. Al guardar se leerá con IA."
            : "Ajustá el área del domicilio. Al guardar se leerá con IA."
        }
        onCancel={cancelarCrop}
        onSave={guardarRecorte}
      />
    </>
  );
}